import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import mastercard from '../../../images/mastercard.svg';
import visa from '../../../images/visa.svg';
import cb from '../../../images/cb.svg';
import { Checkbox } from '../../fields/Checkbox';
import { CaseAdapter } from '@shared/CaseAdapter';
import Btn from '../../buttons/Btn';
import { PromoInput } from '@shared/PromoInput';

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  margin-top: 24px;
`;

const Text = styled.p`
  font-size: 18px;
  font-weight: 400;
  color: #354354;
`;

const PaymentForm = styled.form`
  display: none;
`;

const PaymentIcon = styled.img`
  width: 35px;
  height: 24px;
  object-fit: contain;
  margin-right: 8px;

  &:last-child {
    margin-right: 0;
  }
`;

const Subtext = styled.p`
  font-size: 12px;
  font-weight: 400;
  color: #9aa1a9;
`;

const PaymentIconContainer = styled.div`
  display: flex;
  margin-top: 25px;
  margin-bottom: 8px;
`;

const TermsLink = styled.a`
  color: #4a90e2;
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }
`;

const StyledBtn = styled(Btn)`
  margin-top: 20px;
  width: 100%;
  max-width: 230px;
`;

type PaymentParams = {
  [key: string]: string | number;
  signature: string;
};

type PaymentData = {
  link: string;
  params: PaymentParams;
};

type PaymentConfirmationProps = {
  paymentData: PaymentData;
  isLoading?: boolean;
  validatePointReservation: {
    (orderId: string | number): void;
  };
  validatePromoCode?: (code: string) => Promise<void>;
  errorStatus?: number;
  promoValidationStatus?: number;
  price: number;
  newPrice?: number;
};

const PaymentConfirmation = ({
  paymentData,
  validatePointReservation,
  validatePromoCode,
  errorStatus,
  promoValidationStatus,
  price,
  newPrice,
}: PaymentConfirmationProps) => {
  const formRef = useRef<HTMLFormElement>(null);

  const [termsAccepted, setTermsAccepted] = useState(false);
  const handleCheckboxChange = () => {
    setTermsAccepted(!termsAccepted);
  };

  const termsLabel = (
    <>
      J'accepte les{' '}
      <TermsLink
        href="https://www.actiroute.com/conditions-generales-de-vente/"
        target="_blank"
        rel="noopener noreferrer"
      >
        Conditions générales de vente
      </TermsLink>{' '}
      d'Actiroute.
    </>
  );

  if (!paymentData || !paymentData.params) {
    return null;
  }

  const formattedPaymentData = CaseAdapter.toBackFormat(paymentData.params);

  useEffect(() => {
    validatePointReservation(paymentData.params?.vads_order_id);
  }, [paymentData, validatePointReservation]);

  const submitForm = (e: React.MouseEvent) => {
    e.preventDefault();

    if (errorStatus === 422) {
      return;
    }

    if (formRef.current && paymentData && paymentData.params) {
      localStorage.removeItem('stage_booking_promo');
      formRef.current.submit();
    }
  };

  return (
    <Container>
      <PaymentForm method="POST" action={paymentData.link} ref={formRef}>
        {Object.entries(formattedPaymentData).map(([key, value]) => (
          <input key={key} type="hidden" name={key} value={value.toString()} />
        ))}
      </PaymentForm>
      <PromoInput
        onApplyPromo={validatePromoCode}
        onSuccess={() => {}}
        price={price}
        newPrice={newPrice}
        promoValidationStatus={promoValidationStatus}
      />
      <Text>Vous allez effectuer votre paiement auprès de notre partenaire Actiroute</Text>
      <Checkbox
        name="acceptTerms"
        label={termsLabel}
        isChecked={termsAccepted}
        onChange={handleCheckboxChange}
        error={false}
      />
      <PaymentIconContainer>
        <PaymentIcon src={mastercard} alt="mastercard" />
        <PaymentIcon src={visa} alt="visa" />
        <PaymentIcon src={cb} alt="cb" />
      </PaymentIconContainer>
      <Subtext>
        Vous y êtes presque! Munissez-vous de votre carte bleue, vous allez être dirigé(e) sur
        l'espace de paiement de Lyra
      </Subtext>
      <StyledBtn
        type="submit"
        text="Effectuer le paiement"
        block
        disabled={!termsAccepted}
        isLoading={false}
        onClick={submitForm}
      />
    </Container>
  );
};

export { PaymentConfirmation };
